/**
 * <pinch-zoom-image> — zoomable image for the PDP fullscreen gallery.
 *
 * Mobile: pinch to zoom (1–3×), double-tap to toggle 2.5×, one-finger pan when zoomed,
 *         bounds-constrained, rAF eased animation. (Core gesture math from the proven
 *         implementation used on another store.)
 * Desktop: double-click toggles zoom (2.5×) at the click point, drag to pan, zoom cursor,
 *          double-click again resets.
 *
 * Coordination: dispatches bubbling `zoom:start` / `zoom:end` CustomEvents and reflects
 * `data-zoomed` so the parent slider can lock swiping + hide thumbnails while zoomed.
 * `resetZoom()` is public (slider calls it before navigating).
 */
if (!customElements.get('pinch-zoom-image')) {
  class PinchZoomImage extends HTMLElement {
    constructor() {
      super();
      this.zoomFactor = 1;
      this.initialX = 0;
      this.initialY = 0;
      this.currentX = 0;
      this.currentY = 0;

      this.minZoomFactor = 1.2;
      this.zoomTarget = 2.5;
      this.transitionDuration = 250;
      this.animating = false;

      this.isPinching = false;
      this.isDoubleTap = false;
      this.lastTouchTime = 0;
      this.touchStarted = false;

      // desktop
      this.isDragging = false;
    }

    connectedCallback() {
      this.img = this.querySelector('img');
      if (!this.img) return;

      this._onTouchStart = this.handleTouchStart.bind(this);
      this._onTouchMove = this.handleTouchMove.bind(this);
      this._onTouchEnd = this.handleTouchEnd.bind(this);
      this.addEventListener('touchstart', this._onTouchStart, { passive: false });
      this.addEventListener('touchmove', this._onTouchMove, { passive: false });
      this.addEventListener('touchend', this._onTouchEnd);

      // Desktop pointer (mouse): click toggles zoom, drag pans. Kill the native
      // image drag (ghost image / link cursor) so our custom pan works cleanly.
      this._onPointerDown = this.handlePointerDown.bind(this);
      this._onPointerMove = this.handlePointerMove.bind(this);
      this._onPointerUp = this.handlePointerUp.bind(this);
      this._onDragStart = (e) => e.preventDefault();
      this.addEventListener('pointerdown', this._onPointerDown);
      this.addEventListener('dragstart', this._onDragStart);
      this.img.draggable = false;
      this.img.style.webkitUserDrag = 'none';
      this.img.style.userSelect = 'none';
      this.img.style.transformOrigin = 'center center';

      this.set2dTransform();
    }

    disconnectedCallback() {
      this.removeEventListener('touchstart', this._onTouchStart);
      this.removeEventListener('touchmove', this._onTouchMove);
      this.removeEventListener('touchend', this._onTouchEnd);
      this.removeEventListener('pointerdown', this._onPointerDown);
      this.removeEventListener('dragstart', this._onDragStart);
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
    }

    get isZoomed() {
      return this.zoomFactor > 1;
    }

    #emitZoom(started) {
      if (started) {
        this.setAttribute('data-zoomed', '');
        this.dispatchEvent(new CustomEvent('zoom:start', { bubbles: true }));
      } else {
        this.removeAttribute('data-zoomed');
        this.dispatchEvent(new CustomEvent('zoom:end', { bubbles: true }));
      }
    }

    /* ── Mobile touch ─────────────────────────────────────────────── */
    handleTouchStart(e) {
      if (this.animating) { e.preventDefault(); return; }

      if (e.touches.length === 2) {
        e.preventDefault();
        this.isPinching = true;
        this.initialZoomFactor = this.zoomFactor;
        this.initialDistance = Math.hypot(
          e.touches[0].pageX - e.touches[1].pageX,
          e.touches[0].pageY - e.touches[1].pageY
        );
      } else if (e.touches.length === 1) {
        this.touchStarted = true;
        this.isDoubleTap = this.lastTouchTime && Date.now() - this.lastTouchTime <= 300;
        this.lastTouchTime = Date.now();
        if (this.zoomFactor > 1) {
          e.preventDefault(); // pan — suppress slider swipe while zoomed
          this.initialX = e.touches[0].pageX - this.currentX;
          this.initialY = e.touches[0].pageY - this.currentY;
        }
      }
    }

    handleTouchMove(e) {
      if (this.animating) { e.preventDefault(); return; }

      if (this.isPinching && e.touches.length === 2) {
        e.preventDefault();
        const newDistance = Math.hypot(
          e.touches[0].pageX - e.touches[1].pageX,
          e.touches[0].pageY - e.touches[1].pageY
        );
        const delta = newDistance / this.initialDistance;
        const was = this.zoomFactor;
        this.zoomFactor = Math.max(1, Math.min(3, this.initialZoomFactor * delta));
        if (was <= 1 && this.zoomFactor > 1) this.#emitZoom(true);
        this.updateTransform();
      } else if (e.touches.length === 1 && !this.isPinching && this.touchStarted && this.zoomFactor > 1) {
        e.preventDefault(); // pan — suppress slider swipe while zoomed
        this.currentX = e.touches[0].pageX - this.initialX;
        this.currentY = e.touches[0].pageY - this.initialY;
        this.updateTransform();
      }
    }

    handleTouchEnd(e) {
      if (this.animating) { e.preventDefault(); return; }
      if (e.touches.length !== 0) return;

      if (this.isPinching) {
        this.isPinching = false;
        if (this.zoomFactor < this.minZoomFactor) {
          this.resetZoom();
        } else {
          this.updateTransform();
          this.set2dTransform();
          this.constrainTransform();
        }
      } else if (this.isDoubleTap) {
        if (this.zoomFactor === 1) {
          const oldZoom = this.zoomFactor;
          this.zoomFactor = this.zoomTarget;
          this.#emitZoom(true);
          this.animateTransform(this.currentX, this.currentY, oldZoom, 0, 0, this.zoomFactor);
        } else {
          this.resetZoom();
        }
      } else {
        this.touchStarted = false;
        this.updateTransform();
        this.set2dTransform();
        this.constrainTransform();
      }
    }

    /* ── Desktop pointer: click toggles zoom, drag pans ───────────── */
    handlePointerDown(e) {
      if (e.pointerType === 'touch' || this.animating) return;
      e.preventDefault(); // stop native image drag
      this._pDown = true;
      this._moved = false;
      this._startX = e.clientX;
      this._startY = e.clientY;
      if (this.zoomFactor > 1) {
        this.initialX = e.clientX - this.currentX;
        this.initialY = e.clientY - this.currentY;
      }
      try { this.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
      window.addEventListener('pointermove', this._onPointerMove);
      window.addEventListener('pointerup', this._onPointerUp);
    }

    handlePointerMove(e) {
      if (!this._pDown) return;
      if (Math.abs(e.clientX - this._startX) + Math.abs(e.clientY - this._startY) > 4) this._moved = true;
      if (this.zoomFactor > 1 && this._moved) {
        this.currentX = e.clientX - this.initialX;
        this.currentY = e.clientY - this.initialY;
        this.style.cursor = 'grabbing';
        this.updateTransform();
      }
    }

    handlePointerUp(e) {
      if (!this._pDown) return;
      this._pDown = false;
      try { this.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      this.style.cursor = '';
      if (!this._moved) {
        // treated as a click → toggle zoom
        if (this.zoomFactor === 1) this.#zoomInAt(e.clientX, e.clientY);
        else this.resetZoom();
      } else {
        this.set2dTransform();
        this.constrainTransform();
      }
    }

    #zoomInAt(clientX, clientY) {
      const rect = this.getBoundingClientRect();
      const s = this.zoomTarget;
      const tX = (clientX - (rect.left + rect.width / 2)) * (1 - s);
      const tY = (clientY - (rect.top + rect.height / 2)) * (1 - s);
      this.zoomFactor = s;
      this.#emitZoom(true);
      this.animateTransform(0, 0, 1, tX, tY, s);
    }

    /* ── Shared ───────────────────────────────────────────────────── */
    constrainTransform() {
      const rect = this.img.getBoundingClientRect();
      let newX = this.currentX;
      let newY = this.currentY;

      if (window.innerWidth >= rect.width) newX = 0;
      else if (rect.left > 0) newX -= rect.left;
      else if (window.innerWidth - rect.right > 0) newX += window.innerWidth - rect.right;

      if (window.innerHeight >= rect.height) newY = 0;
      else if (rect.top > 0) newY -= rect.top;
      else if (window.innerHeight - rect.bottom > 0) newY += window.innerHeight - rect.bottom;

      if (newX !== this.currentX || newY !== this.currentY) {
        this.animateTransform(this.currentX, this.currentY, this.zoomFactor, newX, newY, this.zoomFactor);
        this.currentX = newX;
        this.currentY = newY;
      }
    }

    resetZoom() {
      const wasZoomed = this.isZoomed;
      this.animateTransform(this.currentX, this.currentY, this.zoomFactor, 0, 0, 1);
      this.style.cursor = '';
      if (wasZoomed) this.#emitZoom(false);
    }

    updateTransform() {
      this.img.style.transform =
        `translate3d(${this.currentX}px, ${this.currentY}px, 0) scale3d(${this.zoomFactor}, ${this.zoomFactor}, 1)`;
    }

    set2dTransform() {
      this.img.style.transform =
        `translate(${this.currentX}px, ${this.currentY}px) scale(${this.zoomFactor}, ${this.zoomFactor})`;
    }

    animateTransform(startX, startY, startZoom, endX, endY, endZoom) {
      this.animating = true;
      const startTime = performance.now();
      const step = (now) => {
        const elapsed = now - startTime;
        if (elapsed >= this.transitionDuration) {
          this.currentX = endX; this.currentY = endY; this.zoomFactor = endZoom;
          this.updateTransform(); this.set2dTransform();
          this.animating = false;
          return;
        }
        const p = this.easeOutCubic(Math.min(1, elapsed / this.transitionDuration));
        this.currentX = startX + (endX - startX) * p;
        this.currentY = startY + (endY - startY) * p;
        this.zoomFactor = startZoom + (endZoom - startZoom) * p;
        this.updateTransform();
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }

    easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
  }

  customElements.define('pinch-zoom-image', PinchZoomImage);
}
